import {
  TagInput,
  type TagInputTag,
} from "@/components/common/TagInput/TagInput"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useDeleteQuestionnaireAnsweringContext,
  useGetQuestionnaireAnsweringContext,
  useSaveQuestionnaireAnsweringContext,
} from "@/services/api/questionnaireService"
import { isLimitError } from "@/utils/limitErrors"
import { useEffect, useRef, useState } from "react"

// Mirrors the backend limits in turnus_ai/models.py (ANSWERING_CONTEXT_*); change both together.
export const ANSWERING_CONTEXT_MAX_TEXT_CHARS = 2000
export const ANSWERING_CONTEXT_MAX_TERMS = 10
export const ANSWERING_CONTEXT_TERM_MIN_CHARS = 2
export const ANSWERING_CONTEXT_TERM_MAX_CHARS = 40

const hasAngleBrackets = (value: string) => /[<>]/.test(value)

interface AnsweringContextDialogProps {
  questionnaireId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The questionnaire already has generated answers; offers regeneration after a save. */
  hasAnswers: boolean
  /** Called when the user chooses to regenerate; the page runs the existing auto-fill. */
  onRegenerate: () => void
}

export const AnsweringContextDialog = ({
  questionnaireId,
  open,
  onOpenChange,
  hasAnswers,
  onRegenerate,
}: AnsweringContextDialogProps) => {
  const { translate } = useTranslation()
  const {
    data: saved,
    isSuccess,
    isError,
    dataUpdatedAt,
  } = useGetQuestionnaireAnsweringContext(questionnaireId, open)
  const saveAnsweringContext = useSaveQuestionnaireAnsweringContext()
  const deleteAnsweringContext = useDeleteQuestionnaireAnsweringContext()

  const [text, setText] = useState("")
  const [tags, setTags] = useState<TagInputTag[]>([])
  // Fields are filled from the server once per opening, and only from a fetch that completed
  // AFTER the dialog opened — never from data the query already had cached from before, which
  // could be a newer context someone else saved while this dialog sat closed.
  const [hydrated, setHydrated] = useState(false)

  // Mirrors `dataUpdatedAt` into a ref so the open-transition effect below can read "the value
  // as it stood right before this opening" without depending on it — a dependency would make
  // that effect re-run (and re-capture the baseline) on every fetch, never letting it fall
  // behind the value hydration compares against.
  const dataUpdatedAtRef = useRef(dataUpdatedAt)
  useEffect(() => {
    dataUpdatedAtRef.current = dataUpdatedAt
  }, [dataUpdatedAt])

  const wasOpenRef = useRef(open)
  // null: nothing to protect against (the dialog was already open on this component's very
  // first render, so there is no earlier cached value to distrust). Set on every genuine
  // closed -> open transition to `dataUpdatedAt` as it stood right before this opening;
  // hydration below then waits for a fetch that completes after that point.
  const openBaselineRef = useRef<number | null>(null)

  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = open
    if (open) {
      if (!wasOpen) {
        openBaselineRef.current = dataUpdatedAtRef.current
      }
    } else {
      setHydrated(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || hydrated || !isSuccess || !saved) return
    const baseline = openBaselineRef.current
    if (baseline !== null && dataUpdatedAt <= baseline) return
    setText(saved.text)
    setTags(saved.terms.map((term) => ({ id: term, text: term })))
    setHydrated(true)
  }, [open, saved, hydrated, isSuccess, dataUpdatedAt])

  // Normalized the same way the save payload is.
  const currentBody = {
    text: text.trim(),
    terms: tags.map((tag) => tag.text.trim()).filter(Boolean),
  }

  const textHasBrackets = hasAngleBrackets(text)
  const termsHaveBrackets = tags.some((tag) => hasAngleBrackets(tag.text))
  const hasValidationError = textHasBrackets || termsHaveBrackets
  // Gated on `hydrated`, not `isSuccess` alone: `isSuccess` can already be true from a stale
  // cache before the post-open fetch has landed, while the form still shows empty fields —
  // enabling Save in that window would let a click erase the real saved context.
  // Save and Remove share one busy flag: a Save started while a Remove is in flight (or the
  // other way round) would let the later response undo what the user just did.
  const mutationPending =
    saveAnsweringContext.isPending || deleteAnsweringContext.isPending
  const canSave = !hasValidationError && hydrated && !mutationPending

  // A context is currently persisted for this questionnaire (not the same as the form having
  // unsaved edits) — offers Remove only once we actually know that, and only while it's true.
  const hasSavedContext =
    hydrated && !!saved && (saved.text.trim() !== "" || saved.terms.length > 0)

  const handleSave = async () => {
    try {
      await saveAnsweringContext.mutateAsync({
        questionnaireId,
        body: currentBody,
      })
      toast.success(
        translate("ANSWERING_CONTEXT/SAVED"),
        undefined,
        hasAnswers
          ? {
              action: {
                label: translate("ANSWERING_CONTEXT/REGENERATE"),
                onClick: onRegenerate,
              },
            }
          : undefined,
      )
      onOpenChange(false)
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("ERROR/SOMETHING_WENT_WRONG"))
      }
    }
  }

  const handleRemove = async () => {
    try {
      await deleteAnsweringContext.mutateAsync({ questionnaireId })
      toast.success(translate("ANSWERING_CONTEXT/REMOVED"))
      onOpenChange(false)
    } catch {
      toast.error(translate("ERROR/SOMETHING_WENT_WRONG"))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{translate("ANSWERING_CONTEXT/TITLE")}</DialogTitle>
          <DialogDescription>
            {translate("ANSWERING_CONTEXT/DESCRIPTION")}
          </DialogDescription>
        </DialogHeader>

        {isError ? (
          <p className="rounded-md border border-destructive px-3 py-2 text-sm text-destructive">
            {translate("ANSWERING_CONTEXT/LOAD_ERROR")}
          </p>
        ) : (
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="answering-context-text">
                {translate("ANSWERING_CONTEXT/TEXT_LABEL")}
              </Label>
              <Textarea
                id="answering-context-text"
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder={translate("ANSWERING_CONTEXT/TEXT_PLACEHOLDER")}
                maxLength={ANSWERING_CONTEXT_MAX_TEXT_CHARS}
                rows={5}
                disabled={!hydrated}
                aria-invalid={textHasBrackets}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {textHasBrackets ? (
                    <span className="text-destructive">
                      {translate("ANSWERING_CONTEXT/ANGLE_BRACKETS_ERROR")}
                    </span>
                  ) : null}
                </span>
                <span>
                  {translate("ANSWERING_CONTEXT/CHAR_COUNT", {
                    count: text.length,
                    max: ANSWERING_CONTEXT_MAX_TEXT_CHARS,
                  })}
                </span>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="answering-context-terms">
                {translate("ANSWERING_CONTEXT/TERMS_LABEL")}
              </Label>
              <TagInput
                id="answering-context-terms"
                tags={tags}
                onTagsChange={setTags}
                placeholder={translate("ANSWERING_CONTEXT/TERMS_PLACEHOLDER")}
                disabled={!hydrated}
                maxTags={ANSWERING_CONTEXT_MAX_TERMS}
                minLength={ANSWERING_CONTEXT_TERM_MIN_CHARS}
                maxLength={ANSWERING_CONTEXT_TERM_MAX_CHARS}
              />
              <p className="text-xs text-muted-foreground">
                {termsHaveBrackets ? (
                  <span className="text-destructive">
                    {translate("ANSWERING_CONTEXT/ANGLE_BRACKETS_ERROR")}
                  </span>
                ) : (
                  translate("ANSWERING_CONTEXT/TERMS_HELP")
                )}
              </p>
            </div>

            {hasAnswers ? (
              <p className="rounded-md border border-input px-3 py-2 text-sm text-muted-foreground">
                {translate("ANSWERING_CONTEXT/HAS_ANSWERS_NOTE")}
              </p>
            ) : null}
          </div>
        )}

        <DialogFooter className="flex-row items-center justify-end gap-2">
          {hasSavedContext ? (
            <Button
              variant="ghost"
              className="mr-auto text-muted-foreground hover:text-foreground"
              onClick={handleRemove}
              disabled={mutationPending}
            >
              {translate("ANSWERING_CONTEXT/REMOVE")}
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translate("ANSWERING_CONTEXT/CLOSE")}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {translate("ANSWERING_CONTEXT/SAVE")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
